// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ResolvedHeaderLink } from "@stoneage/Overrides/Widgets/Header/HeaderLinkList";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HeaderLinkListFieldModel } from "@stoneage/App/Types/headerLinkList";

const useGetHeaderLinkList = (headerLinkListLinkFields?: HeaderLinkListFieldModel[] | null): ResolvedHeaderLink[] => {
    const safeLinks = headerLinkListLinkFields ?? [];

    const primaryDestinations = safeLinks.map(l => l.fields.destination);
    const subNavItems = safeLinks.flatMap(l => l.fields.l2Links ?? []);
    const subDestinations = subNavItems.map(l => l.fields.destination);

    const { links: resolvedPrimary, hasMissingCategories: primaryMissing } = useGetLinks(primaryDestinations, o => o);
    const { links: resolvedSub, hasMissingCategories: subMissing } = useGetLinks(subDestinations, o => o);

    if (safeLinks.length === 0 || primaryMissing || subMissing) {
        return [];
    }

    let subOffset = 0;
    return safeLinks.map((item, i) => {
        const { overriddenTitle, icon, isMenu, openInNewWindow, l2Links } = item.fields;
        const parentField = safeLinks[i];
        const subLinkCount = l2Links?.length ?? 0;
        const resolvedPrimaryLink = resolvedPrimary[i];

        const children: ResolvedHeaderLink["children"] = (l2Links ?? []).map((subLink, j) => {
            const resolvedSubLink = resolvedSub[subOffset + j];
            return {
                ...resolvedSubLink,
                overriddenTitle: subLink.fields.overriddenTitle,
                openInNewWindow: subLink.fields.openInNewWindow,
            };
        });

        subOffset += subLinkCount;

        return {
            link: {
                url: resolvedPrimaryLink?.url,
                title: resolvedPrimaryLink?.title,
            },
            overriddenTitle,
            icon,
            isMenu,
            openInNewWindow,
            excludeFromNavigation: false,
            children,
        };
    });
};

export default useGetHeaderLinkList;
